/**
 * CR325: Global Event Recorder for Deal Card Editor
 *
 * Captures the last 200 editor lifecycle events to a ring buffer
 * on window.__DC_EDITOR_EVENTS__ for fast triage of state oscillation.
 *
 * Usage:
 *   import { logEvt, copyEvts, clearEvts } from "@/lib/debug/dealCardEditorEvents";
 *   logEvt({ type: "hydrate_start", dealTypeKey: "BONUS", nonce: 3 });
 */

const MAX_EVENTS = 200;

export type EditorEventType =
  // Hydration lifecycle
  | "hydrate_start"
  | "hydrate_commit"
  | "hydrate_end"
  // Async apply
  | "apply_registry"
  | "apply_saved_template"
  | "apply_saved_components"
  | "apply_template_draft"
  | "apply_component_draft"
  | "apply_ignored_stale"
  // Dirty transitions
  | "dirty_transition"
  // Draft persistence
  | "draft_persist_template"
  | "draft_persist_component"
  | "draft_persist_skipped_hydrating"
  | "draft_clear"
  // Save lifecycle
  | "save_template_start"
  | "save_template_success"
  | "save_template_fail"
  | "save_component_start"
  | "save_component_success"
  | "save_component_fail"
  // Violations / anomalies
  | "dirty_violation_template_from_non_template_domain"
  | "dirty_violation_component_from_non_component_domain"
  // Stability mode
  | "stability_mode_on"
  | "stability_mode_off"
  | "manual_refresh"
  // Other
  | "type_switch"
  | "discard"
  | "nonce_increment";

export interface EditorEvent {
  /** Monotonic counter set automatically */
  seq: number;
  /** ISO timestamp */
  ts: string;
  /** Milliseconds since page load (performance.now) */
  t: number;
  type: EditorEventType;
  dealTypeKey?: string;
  nonce?: number;
  hydrating?: boolean;
  templateDirty?: boolean;
  componentDirty?: boolean;
  savedHash?: string;
  effectiveHash?: string;
  note?: string;
}

// Module‐level ring buffer
let events: EditorEvent[] = [];
let seq = 0;

function getBuffer(): EditorEvent[] {
  // Lazy‐init window global
  if (typeof window !== "undefined") {
    if (!(window as any).__DC_EDITOR_EVENTS__) {
      (window as any).__DC_EDITOR_EVENTS__ = events;
    } else {
      events = (window as any).__DC_EDITOR_EVENTS__;
    }
  }
  return events;
}

/**
 * Append an event to the ring buffer.
 */
export function logEvt(
  evt: Omit<EditorEvent, "seq" | "ts" | "t">
): void {
  const buf = getBuffer();
  const entry: EditorEvent = {
    seq: seq++,
    ts: new Date().toISOString(),
    t: Math.round(performance.now()),
    ...evt,
  };
  buf.push(entry);
  // Trim to max
  if (buf.length > MAX_EVENTS) {
    buf.splice(0, buf.length - MAX_EVENTS);
  }

  // Also log to console in debug builds
  if (import.meta.env.DEV) {
    console.debug(`[DC_EVT] ${entry.type}`, entry);
  }
}

/**
 * Copy all events to clipboard as formatted JSON.
 * Returns true on success.
 */
export async function copyEvts(): Promise<boolean> {
  const buf = getBuffer();
  const json = JSON.stringify(buf, null, 2);
  try {
    await navigator.clipboard.writeText(json);
    return true;
  } catch {
    console.log("[CR325] Bounce log (fallback):", json);
    return false;
  }
}

/**
 * Clear the event buffer.
 */
export function clearEvts(): void {
  const buf = getBuffer();
  buf.length = 0;
  seq = 0;
}

/**
 * Get the last N events (default 5).
 */
export function getLastEvents(n = 5): EditorEvent[] {
  const buf = getBuffer();
  return buf.slice(-n);
}

/**
 * Get the full event count.
 */
export function getEventCount(): number {
  return getBuffer().length;
}
